import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { formCss } from '../css/form';
import { resetCss } from '../css/reset';
import { stackCss } from '../css/stack';
import { typeCss } from '../css/type';
import { debounce } from '../decorators';
import { makeRangeDescription } from '../util';
import { wordListPromise } from '../wordList';
import '../wordListElement';

// Exported for benchmarkElement.ts
export function crosswordFilter(words: string[], pattern: string) {
  // 'c.t' -> /^c[a-z]t$/
  const patternRe = new RegExp(`^${pattern.replace(/[^a-z]/g, '[a-z]')}$`);
  return words.filter(word => patternRe.test(word));
}

// Make everything that's not a letter turn into a hyphen.
const normalizePattern = (pattern: string) =>
  pattern.toLowerCase().replace(/[^a-z]/g, '-');

@customElement('mw-crossword-page')
export class CrosswordPageElement extends LitElement {
  @state()
  pattern = 'cr---w--d';
  @state()
  words: string[] = [];

  limit = 1000;  // don't render more than this many words at once
  @state()
  rangeDescription = ''; // "first 1000 of 2345"
  @state()
  loading = true;
  @state()
  error: unknown;

  constructor() {
    super();
    this.updateResults();
  }

  @debounce(100)
  updateResults() {
    this.loading = true;
    this.error = false;
    wordListPromise
      .then(wordList => {
        this.words = crosswordFilter(wordList, this.pattern);
      }, reason => {
        this.words = [];
        this.error = reason;
      })
      .finally(() => {
        this.loading = false;
        this.rangeDescription = makeRangeDescription(this.words.length, this.limit);
      });
  };

  onPatternChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.pattern = normalizePattern(target.value);
    // A hack? to make sure the input's displayed value updates when the new
    // value undoes the change the user just made. Without this, the framework
    // sees "no change" and the user's change isn't undone.
    // https://github.com/Polymer/lit-element/issues/144#issuecomment-468098257
    target.value = this.pattern;
    this.updateResults();
  };

  static get styles() {
    return [
      resetCss,
      stackCss,
      typeCss,
      formCss,
    ];
  }

  render() {
    return html`
      <div class="stack">
        <p>Type in your progress, indicating unsolved letters with a <samp>-</samp> (hyphen).</p>

        <div>
          <label for="pattern">Your progress</label>
          <input
              id="pattern"
              class="puzzle-input"
              autofocus
              spellcheck="false"
              .value="${this.pattern}"
              @input="${this.onPatternChange}">
        </div>

        <div>
          <h2>See the ${this.pattern && this.rangeDescription} possible words</h2>

          ${this.loading ? html`<p>Loading&hellip;</p>` : null}

          ${this.error ? html`
          <p title="${JSON.stringify(this.error)}">
            Something&rsquo;s broken &mdash; we couldn't find your words.
          </p>
          ` : null}

          <mw-word-list .words="${this.words}" .limit="${this.limit}"></mw-word-list>
        </div>

        <div>
          <h3>About the results</h3>
          <p>
            The results are only English "dictionary" words, so they
            won't help you find crossword answers that are phrases,
            abbreviations, or proper nouns.
          </p>
          <p>
            The words come from the ENABLE word list (Y2K update), described at
            <a href="http://archive.aspell.net/enable2k">archive.aspell.net/enable2k</a>.
          </p>
        </div>
      </div>
    `;
  }
}
